import RosterBackendService from './RosterBackendService';
import {
  Environments,
  GetLTIPlatformsResponse,
  GetSSOProvidersResponse,
  GetSSOSitesResponse, LTIConfigDTO,
  LTIPlatformDTO,
  ProviderSiteDTO,
  SSOProviderDTO
} from '@beable/roster-backend-client-v1';

/*                          PROVIDERS                    */
// Create
async function addNewProvider(payload: SSOProviderDTO, enableAllEnvs: boolean): Promise<SSOProviderDTO | string | null> {
    const response = await RosterBackendService.addNewProvider(payload, enableAllEnvs);
    if (response.isErr()) {
        if (JSON.stringify(response.error.statusCode).includes('503') && !enableAllEnvs) { return `Provider already exists in this environment.`; }
        if (JSON.stringify(response.error.statusCode).includes('503') && enableAllEnvs) { return `Provider already defined in all envs.`; }
        return null;
    }
    return response.value;
}

//Read
async function getAllProviders(page?: number): Promise<GetSSOProvidersResponse> {
    const response = await RosterBackendService.getAllProviders(page ?? 0);
    if (response.isErr()) return { data: [], pages: 0 };
    return response.value;
}

//Update
async function updateProvider(payload: SSOProviderDTO): Promise<SSOProviderDTO | string | null> {
    const response = await RosterBackendService.updateProvider(payload);
    if (response.isErr()) {
        if (JSON.stringify(response.error.statusCode).includes('503')) { return `Provider already exists in this environment.`; }
        return null;
    }
    return response.value;
}

//Delete
async function deleteProvider(id: string): Promise<boolean> {
    const response = await RosterBackendService.deleteProvider(id);
    if (response.isErr()) return false;
    return response.value;
}

//Search
async function searchProviders(searchTerm: string): Promise<GetSSOProvidersResponse | null> {
    const searchResult = await RosterBackendService.searchProviders(searchTerm);
    if (searchResult.isErr()) return null;
    return searchResult.value;
}

/*                          PROVIDER SITES                    */
// Create
async function addNewProviderSite(payload: ProviderSiteDTO, enableAllEnvs: boolean, env: string) {
    const response = await RosterBackendService.addNewProviderSite(payload, enableAllEnvs, env);
    const success: any = 'Provider Site added successfully!';
    if (response.isErr()) {      
      if(response.error.result.isJust() && response.error.result.value?.message) {
        const message = response.error.result.value?.message;
        return { error: message?.replace('Internal Server Error: ', '') };
      }
      if(response.error.message) { return { error: response.error.message }; }
      if (JSON.stringify(response.error.statusCode).includes('503') && !enableAllEnvs) { return { error: `Provider already exists for this site and environment.` }; }
      if (JSON.stringify(response.error.statusCode).includes('503') && enableAllEnvs) { return { error: `Provider already defined for siteId ${payload.siteId} in all envs.` }; }
      return { error: 'Failed to add this site!' };
    }
    return { success };
}

//Read
async function getProviderSites(page: number): Promise<GetSSOSitesResponse> {

    const response = await RosterBackendService.getProviderSites(page);
    if (response.isErr()) return { data: [], pages: 0 };
    return response.value;

}

//Update
async function updateProviderSite(payload: ProviderSiteDTO, env: string) {
  const response = await RosterBackendService.updateProviderSite(payload, env);
  const success: any = 'Site edited successfully!';
  if (response.isErr()) {
    if(response.error.result.isJust() && response.error.result.value?.message) {
      const message = response.error.result.value?.message;
      return { error: message?.replace('Internal Server Error: ', '') };
    }
    if(response.error.message) { return { error: response.error.message }; }
    if (JSON.stringify(response.error.statusCode).includes('503')) { return { error: `Provider already added for siteId ${payload.siteId} in ${env}.` }; }
    return { error: 'Failed to edit this site!' };
  }
  return { success };
}

//Delete
async function deleteProviderSite(id: string): Promise<boolean> {

    const response = await RosterBackendService.deleteProviderSite(id);
    if (response.isErr()) return false;
    return response.value;

}

//Search
async function searchProviderSites(searchTerm: string): Promise<GetSSOSitesResponse | null> {

    const searchResult = await RosterBackendService.searchProviderSites(searchTerm);
    if (searchResult.isErr()) return null;
    return searchResult.value;

}

/*
* LTI Providers
* */

async function addNewLTIPlatform(payload: LTIPlatformDTO, enableAllEnvs: boolean): Promise<LTIPlatformDTO | string | null> {
  const response = await RosterBackendService.addNewLTIPlatform(payload, enableAllEnvs);
  if (response.isErr()) {
    if (JSON.stringify(response.error.statusCode).includes('503') && !enableAllEnvs) { return `LTI Platform already exists in this environment.`; }
    if (JSON.stringify(response.error.statusCode).includes('503') && enableAllEnvs) { return `LTI Platform already defined in all envs.`; }
    return null;
  }
  return response.value;
}

async function getLTIPlatforms(page: number): Promise<GetLTIPlatformsResponse> {
  const response = await RosterBackendService.getLTIPlatforms({ page });
  if (response.isErr()) return { data: [], pages: 0 };
  return response.value;
}

async function updateLTIPlatform(payload: LTIPlatformDTO): Promise<LTIPlatformDTO | string | null> {
  const response = await RosterBackendService.updateLTIPlatform(payload);
  if (response.isErr()) {
    if (JSON.stringify(response.error.statusCode).includes('503')) { return `LTI Platform already exists in this environment.`; }
    return null;
  }
  return response.value;
}

async function deleteLTIPlatform(id: string): Promise<boolean> {
  const response = await RosterBackendService.deleteLTIPlatform({ id });
  if (response.isErr()) return false;
  return response.value;
}

async function searchLTIPlatforms(searchTerm: string): Promise<GetLTIPlatformsResponse | null> {
  const searchResult = await RosterBackendService.searchLTIPlatforms(searchTerm);
  if (searchResult.isErr()) return null;
  return searchResult.value;
}

async function getLTIConfig(env: Environments): Promise<{ config: LTIConfigDTO } | null> {
  const response = await RosterBackendService.getLTIConfig(env);
  if (response.isErr()) return null;
  return response.value;
}


export default {
  addNewProvider,
  getAllProviders,
  updateProvider,
  deleteProvider,
  searchProviders,
  addNewProviderSite,
  getProviderSites,
  updateProviderSite,
  deleteProviderSite,
  searchProviderSites,
  addNewLTIPlatform,
  getLTIPlatforms,
  updateLTIPlatform,
  deleteLTIPlatform,
  searchLTIPlatforms,
  getLTIConfig
};
